<script lang="ts" setup>
import { COACH_RESET_USER_MUTATION } from '~/graphql/documents/coach';
import {
  UNIFIED_DEV_LANE_SEED_CANDIDATES,
  UNIFIED_DEV_SET_ONBOARDING_EXPLORE_CAREER,
  UNIFIED_DEV_SET_ONBOARDING_EXPLORE_CONTENT,
  UNIFIED_DEV_SET_ONBOARDING_EXPLORE_INDUSTRY,
  UNIFIED_DEV_SET_ONBOARDING_UTM_CAMPAIGN,
} from '~/graphql/documents/unified';

interface LaneCandidate {
  id: string;
  name: string;
}

const client = useUrqlClient();
const { show: showToast } = useToast();
const isLoading = ref(false);

const occupations = ref<LaneCandidate[]>([]);
const industries = ref<LaneCandidate[]>([]);
const collections = ref<LaneCandidate[]>([]);
const utmKeys = ref<string[]>([]);

const pickedOccupationId = ref('');
const pickedIndustryId = ref('');
const pickedCollectionId = ref('');
const pickedUtmKey = ref('');

// Campaign copy is authored per key, so the panel can only offer the keys this environment has
// rows for — an environment with none has no campaign lane to enter.
const hasUtmKeys = computed(() => utmKeys.value.length > 0);

// The section renders its slot only while it is open, so this runs the first time a developer
// opens it — and on re-opens the client's cache answers, so the index is asked once.
onMounted(async () => {
  const result = await client.query(UNIFIED_DEV_LANE_SEED_CANDIDATES, {});

  if (result.error) {
    showToast({ variant: 'error', message: result.error.message });

    return;
  }

  const candidates = result.data?.unifiedDevLaneSeedCandidates;

  if (!candidates) {
    return;
  }

  occupations.value = candidates.occupations.map(({ id, name }) => ({ id, name }));
  industries.value = candidates.industries.map(({ id, name }) => ({ id, name }));
  collections.value = candidates.collections.map(({ id, name }) => ({ id, name }));
  utmKeys.value = [...candidates.utmKeys];
  pickedUtmKey.value = utmKeys.value[0] ?? '';
});

// A lane is a fresh-user thing, so the button makes a fresh user: reset first (which deletes the
// wallet context a prior seed left too), seed the lane onto the clean slate, then reload — the
// conversation re-inits on the spot and its first session is born through the lane.
//
// The pick is captured at the click and the selects freeze while the pair runs, so what gets
// seeded is what was on screen when the button was pressed. A seed that fails AFTER the reset
// still reloads: the reset already happened, and a page still describing the pre-reset user
// under an error toast would be lying about the one thing that did change.
const enterLane = (label: string, seed: () => Promise<void>) => async () => {
  if (isLoading.value) {
    return;
  }
  isLoading.value = true;
  try {
    const reset = await client.mutation(COACH_RESET_USER_MUTATION, {});
    if (reset.error) {
      isLoading.value = false;
      showToast({
        variant: 'error',
        message: reset.error.message || `Failed to enter the ${label} lane`,
      });

      return;
    }
    await seed();
    showToast({ variant: 'success', message: `${label} lane — user reset, re-opening` });
  }
  catch (err) {
    showToast({
      variant: 'error',
      message: `User was reset, but the ${label} seed failed: ${
        err instanceof Error ? err.message : 'unknown error'} — reloading`,
    });
  }
  window.location.reload();
};

// Nothing picked sends nothing, which is the seed's own "whatever this environment holds" arm.
// Each handler captures its pick at the click, so a select changed mid-flight changes nothing.
const enterCareer = () => {
  const occupationId = pickedOccupationId.value || undefined;

  return enterLane('Career', async () => {
    const result = await client.mutation(UNIFIED_DEV_SET_ONBOARDING_EXPLORE_CAREER, { occupationId });
    if (result.error) {
      throw result.error;
    }
  })();
};

const enterIndustry = () => {
  const industryId = pickedIndustryId.value || undefined;

  return enterLane('Industry', async () => {
    const result = await client.mutation(UNIFIED_DEV_SET_ONBOARDING_EXPLORE_INDUSTRY, { industryId });
    if (result.error) {
      throw result.error;
    }
  })();
};

const enterContent = () => {
  const collectionId = pickedCollectionId.value || undefined;

  return enterLane('Content', async () => {
    const result = await client.mutation(UNIFIED_DEV_SET_ONBOARDING_EXPLORE_CONTENT, { collectionId });
    if (result.error) {
      throw result.error;
    }
  })();
};

const enterUtm = () => {
  const utmKey = pickedUtmKey.value;

  return enterLane('UTM', async () => {
    const result = await client.mutation(UNIFIED_DEV_SET_ONBOARDING_UTM_CAMPAIGN, { utmKey });
    if (result.error) {
      throw result.error;
    }
  })();
};
</script>

<template>
  <div class="px-3 py-1">
    <p class="mb-1.5 text-xs text-muted-foreground">
      Resets the coach user and re-opens the conversation through the lane.
    </p>
    <div class="flex flex-col gap-1.5">
      <div class="flex items-center gap-1.5">
        <select
          v-model="pickedOccupationId"
          class="min-w-0 flex-1 rounded-md border border-border px-1.5 py-0.5 text-xs disabled:opacity-50"
          :disabled="isLoading || occupations.length === 0"
        >
          <option value="">
            (first available)
          </option>
          <option
            v-for="occupation in occupations"
            :key="occupation.id"
            :value="occupation.id"
          >
            {{ occupation.name }}
          </option>
        </select>
        <button
          class="w-20 shrink-0 rounded-md px-2 py-0.5 text-xs font-medium text-white bg-info-foreground hover:bg-info-foreground/90 disabled:opacity-50"
          :disabled="isLoading || occupations.length === 0"
          @click="enterCareer"
        >
          Career
        </button>
      </div>
      <div class="flex items-center gap-1.5">
        <select
          v-model="pickedIndustryId"
          class="min-w-0 flex-1 rounded-md border border-border px-1.5 py-0.5 text-xs disabled:opacity-50"
          :disabled="isLoading || industries.length === 0"
        >
          <option value="">
            (first available)
          </option>
          <option
            v-for="industry in industries"
            :key="industry.id"
            :value="industry.id"
          >
            {{ industry.name }}
          </option>
        </select>
        <button
          class="w-20 shrink-0 rounded-md px-2 py-0.5 text-xs font-medium text-white bg-info-foreground hover:bg-info-foreground/90 disabled:opacity-50"
          :disabled="isLoading || industries.length === 0"
          @click="enterIndustry"
        >
          Industry
        </button>
      </div>
      <div class="flex items-center gap-1.5">
        <select
          v-model="pickedCollectionId"
          class="min-w-0 flex-1 rounded-md border border-border px-1.5 py-0.5 text-xs disabled:opacity-50"
          :disabled="isLoading || collections.length === 0"
        >
          <option value="">
            (first available)
          </option>
          <option
            v-for="collection in collections"
            :key="collection.id"
            :value="collection.id"
          >
            {{ collection.name }}
          </option>
        </select>
        <button
          class="w-20 shrink-0 rounded-md px-2 py-0.5 text-xs font-medium text-white bg-info-foreground hover:bg-info-foreground/90 disabled:opacity-50"
          :disabled="isLoading || collections.length === 0"
          @click="enterContent"
        >
          Content
        </button>
      </div>
      <div class="flex items-center gap-1.5">
        <select
          v-model="pickedUtmKey"
          class="min-w-0 flex-1 rounded-md border border-border px-1.5 py-0.5 text-xs disabled:opacity-50"
          :disabled="isLoading || !hasUtmKeys"
        >
          <option v-if="!hasUtmKeys" value="">
            (no campaigns configured)
          </option>
          <option
            v-for="utmKey in utmKeys"
            :key="utmKey"
            :value="utmKey"
          >
            {{ utmKey }}
          </option>
        </select>
        <button
          class="w-20 shrink-0 rounded-md px-2 py-0.5 text-xs font-medium text-white bg-info-foreground hover:bg-info-foreground/90 disabled:opacity-50"
          :disabled="isLoading || !hasUtmKeys"
          @click="enterUtm"
        >
          UTM
        </button>
      </div>
    </div>
  </div>
</template>
